/**
 * Floating shelves (claude-docs/2026-10-30-venus-full-line-design.md, group
 * 3b): an upper bay's front (`type: 'floating-shelves'`), boards on the wall
 * where a box would hang — the one rule the panel, the fill, the drawing and
 * the estimate read.
 *
 * A line sells them in stock lengths — Venus `FLS30` / `FLS36` / `FLS48`,
 * 2" thick, 12" deep; a generic line the same three at our figure — and they
 * are NEVER cut (the user, 2026-10-31: "dont allow cuts": a cut end is raw,
 * and a 2" floating shelf is often hollow). So the front is offered only on a
 * bay exactly one of those lengths, and a bay of any other length says why.
 * The boards run every 12" up from the uppers' bottom line, as many
 * as fit under their top — none up at the cabinet tops (the user,
 * 2026-10-31). No filler stands beside them (`upperTier`): shelves need no
 * scribe. Billed one board each, in the run's
 * colour.
 *
 * Pure.
 */
const { unitsOf, familySizes } = require('./vendorSizes');
const { familyOf } = require('./families');
const { FLOATING_SHELF, floatingShelfCount } = require('./cabinetFrontSpecs');

const IN = 0.0254;
const KIND = 'floating-shelf';
// a bay within this of a stock length is that length (a fill's rounding)
const LENGTH_TOL_IN = 1 / 16;

/**
 * The generic lengths, for every line with no floating shelves of its own:
 * our cost a board, near Venus's cheapest colour (`FLS30` $118.72, `FLS36`
 * $137.76, `FLS48` $150.08 in HF, 2025-11-01 list).
 */
const GENERIC_FLOATING_SHELVES = Object.freeze([
  { w: 30, costEach: 119 },
  { w: 36, costEach: 138 },
  { w: 48, costEach: 150 },
]);

/** Is this upper bay floating shelves rather than a box? */
const isFloatingShelves = (o) => o?.category?.floatingShelves === true;

/**
 * The boards a line sells, shortest first: `{ sku, w, h, d, colours,
 * costEach }` — a vendor's from its catalog, priced by its list in a colour;
 * a generic line's at our figure, any colour.
 */
function floatingShelfUnits(family) {
  if (familySizes(family))
    return unitsOf(family, KIND).map((u) => ({
      sku: u.sku,
      w: u.w,
      h: u.h,
      d: u.d,
      colours: [...(u.colours || [])],
      costEach: null,
    }));
  return GENERIC_FLOATING_SHELVES.map((g) => ({
    sku: null,
    w: g.w,
    h: FLOATING_SHELF.thicknessM / IN,
    d: FLOATING_SHELF.depthM / IN,
    colours: null,
    costEach: g.costEach,
  }));
}

/** The board of exactly this bay's length (metres), or null — never cut. */
function floatingShelfUnitFor(family, widthM) {
  const widthIn = widthM / IN;
  return (
    floatingShelfUnits(family).find(
      (u) => Math.abs(u.w - widthIn) <= LENGTH_TOL_IN
    ) || null
  );
}

/** `30", 36" or 48"` */
const lengthsText = (units) => {
  const ws = units.map((u) => `${u.w}"`);
  return ws.length > 1
    ? `${ws.slice(0, -1).join(', ')} or ${ws[ws.length - 1]}`
    : ws.join('');
};

/**
 * Why this bay cannot wear floating shelves, or null when it can: its length
 * must be one the line sells.
 */
function floatingShelfReason(run, cabinet) {
  const widthM = cabinet?.dimensions?.x;
  if (!(widthM > 0)) return null;
  const family = familyOf(run);
  if (floatingShelfUnitFor(family, widthM)) return null;
  const units = floatingShelfUnits(family);
  if (units.length === 0) return `${family.name} sells no floating shelves`;
  const said = Math.round((widthM / IN) * 8) / 8;
  return `${family.name} sells floating shelves ${lengthsText(units)} long, and they are never cut: this bay is ${said}"`;
}

module.exports = {
  FLOATING_SHELF,
  GENERIC_FLOATING_SHELVES,
  isFloatingShelves,
  floatingShelfUnits,
  floatingShelfUnitFor,
  floatingShelfReason,
  floatingShelfCount,
};
